import { useCallback, useEffect, useState } from "react";
import { faPlus, faPencil, faTrash, faGripVertical } from "@fortawesome/free-solid-svg-icons";
import HelpTooltip from "./HelpTooltip";
import { HELP } from "../utils/thorHelp";
import Icon from "../../common/elements/Icon";
import Button from "../../common/elements/Button";
import StateHandler from "../../common/elements/StateHandler";
import SmallLoadingSpinner from "../../common/elements/SmallLoadingSpinner";
import InfoCard from "../../common/elements/InfoCard";
import useFetchWithMsal from "../../../useFetchWithMsal";
import { thor_api_urls } from "../../common/Constants";
import { buildDataState, buildErrorState, buildLoadingState } from "../../../utils/StateUtils";
import { useThor } from "../context/ThorContext";
import { MetricAddModal, MetricEditModal } from "./MetricModal";
import { serverErrMsg } from "../utils/validators";
import ConfirmDialog from "./ConfirmDialog";

const CATEGORY_COLORS = {
    Primary: 'is-link', PRIMARY: 'is-link',
    Secondary: 'is-info is-light', SECONDARY: 'is-info is-light',
    Guardrail: 'is-warning is-light', GUARDRAIL: 'is-warning is-light',
};

export const MetricsTab = ({ experimentKey }) => {
    const { execute } = useFetchWithMsal();
    const { editMode, addToast } = useThor();

    const [metricsState, setMetricsState] = useState(buildLoadingState([]));
    const [metrics, setMetrics] = useState([]);
    const [certifiedMap, setCertifiedMap] = useState({});
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editMetric, setEditMetric] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dragIndex, setDragIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const fetchMetrics = useCallback(() => {
        setMetricsState(buildLoadingState([]));
        Promise.all([
            execute('GET', thor_api_urls.experimentMetrics(experimentKey)),
            execute('GET', thor_api_urls.metricsRepo).catch(() => null),
        ]).then(([expData, catalogData]) => {
            const list = expData?.metrics ?? expData ?? [];
            setMetricsState(buildDataState(list));
            setMetrics(list);
            if (catalogData) {
                const map = {};
                (catalogData.metrics ?? catalogData ?? []).forEach(m => {
                    map[m.metricId ?? m.id] = m.isCertified ?? m.certified ?? false;
                });
                setCertifiedMap(map);
            }
        }).catch(error => {
            console.error("Failed to fetch metrics:", error);
            setMetricsState(buildErrorState(error, []));
        });
    }, [execute, experimentKey]);

    useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

    const openAdd = () => setAddModalOpen(true);
    const openEdit = (m) => { setEditMetric(m); setEditModalOpen(true); };

    const handleAddSubmit = (batch) => {
        setIsSubmitting(true);
        execute('POST', thor_api_urls.experimentMetricsBatch(experimentKey), batch)
            .then(() => { setAddModalOpen(false); fetchMetrics(); addToast(`${batch.length} metric${batch.length !== 1 ? 's' : ''} added.`, 'success'); })
            .catch(error => { console.error("Failed to add metrics:", error); addToast(serverErrMsg(error, 'Failed to add metrics.'), 'danger'); })
            .finally(() => setIsSubmitting(false));
    };

    const handleEditSubmit = (form) => {
        setIsSubmitting(true);
        const payload = { metricId: editMetric.metricId, label: form.label, eventKey: form.eventKey, category: form.category };
        execute('PUT', thor_api_urls.experimentMetric(experimentKey, editMetric.metricId), payload)
            .then(() => { setEditModalOpen(false); fetchMetrics(); addToast('Metric updated.', 'success'); })
            .catch(error => { console.error("Failed to update metric:", error); addToast(serverErrMsg(error, 'Failed to update metric.'), 'danger'); })
            .finally(() => setIsSubmitting(false));
    };

    const handleDelete = (m) => {
        const metricId = m.metricId ?? m.id;
        setDeletingId(metricId);
        execute('DELETE', thor_api_urls.experimentMetric(experimentKey, metricId))
            .then(() => { fetchMetrics(); addToast('Metric removed.', 'success'); })
            .catch(error => { console.error("Failed to delete metric:", error); addToast(serverErrMsg(error, 'Failed to remove metric.'), 'danger'); })
            .finally(() => setDeletingId(null));
    };

    // ── Drag-to-reorder ───────────────────────────────────────────────────

    const handleDragStart = (index) => setDragIndex(index);
    const handleDragOver = (e, index) => { e.preventDefault(); setDragOverIndex(index); };
    const handleDragEnd = () => { setDragIndex(null); setDragOverIndex(null); };

    const handleDrop = (dropIndex) => {
        if (dragIndex === null || dragIndex === dropIndex) { handleDragEnd(); return; }
        const reordered = [...metrics];
        const [moved] = reordered.splice(dragIndex, 1);
        reordered.splice(dropIndex, 0, moved);
        setMetrics(reordered);
        handleDragEnd();
        execute('POST', thor_api_urls.metricsRearrange(experimentKey), { newMetricIdOrder: reordered.map(m => m.metricId) })
            .then(() => addToast('Order saved.', 'success'))
            .catch(error => { addToast(serverErrMsg(error, 'Failed to save order.'), 'danger'); fetchMetrics(); });
    };

    return (
        <>
            <div className="level mb-3">
                <div className="level-left">
                    <p className="subtitle is-5 mb-0">Metrics</p>
                </div>
                <div className="level-right">
                    <Button className="is-link is-small" onClick={openAdd} disabled={!editMode}>
                        <Icon icon={faPlus} />
                        <span>Add Metric</span>
                    </Button>
                </div>
            </div>

            <div className="message is-info is-small mb-3">
                <div className="message-body py-2 px-3 is-size-7">
                    <strong>Metric requirements:</strong> 1 Primary metric · 1–6 Guardrail metrics · 2–4 Secondary metrics (optional). The #1 metric is used as the primary metric in Heimdall comparison views.
                </div>
            </div>

            {editMode && metrics.length > 1 && (
                <p className="has-text-grey is-size-7 mb-3">
                    Drag the <Icon icon={faGripVertical} className="has-text-grey" /> handle to reorder.
                </p>
            )}

            <StateHandler showLoader={metricsState.loading} showError={metricsState.error}
                          loadingScreen={<SmallLoadingSpinner />}>
                {metrics.length === 0
                    ? (
                        <div className="has-text-centered" style={{ padding: '2rem', color: '#888' }}>
                            <p className="has-text-weight-semibold mb-2" style={{ color: '#363636' }}>No metrics yet</p>
                            <p className="is-size-7 mb-3">Add a <strong>Primary metric</strong> to measure your experiment's success. You'll also need <strong>Guardrail metrics</strong> (1–6) before going live in production.</p>
                            {editMode && <button type="button" className="button is-link is-small" onClick={openAdd}>Add your first metric</button>}
                            {!editMode && <p className="is-size-7 has-text-grey">Enable Edit mode to add metrics.</p>}
                        </div>
                    )
                    : (
                        <table className="table is-fullwidth is-hoverable">
                            <thead>
                                <tr>
                                    {editMode && <th style={{ width: '2rem' }} />}
                                    <th style={{ width: '3rem' }}>#<HelpTooltip text={HELP.metrics.order} position="right" /></th>
                                    <th>Label</th>
                                    <th>Event Key</th>
                                    <th>Category<HelpTooltip text={`Primary: ${HELP.metrics.primary}\n\nSecondary: ${HELP.metrics.secondary}\n\nGuardrail: ${HELP.metrics.guardrail}`} position="top" /></th>
                                    <th className="has-text-centered">Certified<HelpTooltip text={HELP.metrics.certified} position="top" /></th>
                                    <th className="has-text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {metrics.map((m, index) => (
                                    <tr key={m.metricId}
                                        draggable={editMode}
                                        onDragStart={() => handleDragStart(index)}
                                        onDragOver={(e) => handleDragOver(e, index)}
                                        onDrop={() => handleDrop(index)}
                                        onDragEnd={handleDragEnd}
                                        style={{
                                            opacity: dragIndex === index ? 0.4 : 1,
                                            background: dragOverIndex === index && dragIndex !== index ? '#f0f4ff' : undefined,
                                            cursor: editMode ? 'grab' : 'default',
                                        }}>
                                        {editMode && (
                                            <td style={{ color: '#aaa', verticalAlign: 'middle' }}>
                                                <Icon icon={faGripVertical} />
                                            </td>
                                        )}
                                        <td className="has-text-grey is-size-7">{index + 1}</td>
                                        <td className="has-text-weight-semibold" title={m.label}>{m.label}</td>
                                        <td className="has-text-grey is-family-monospace" style={{ fontSize: '0.8rem' }}>{m.eventKey}</td>
                                        <td>
                                            <span className={`tag ${CATEGORY_COLORS[m.category] ?? 'is-light'}`}>
                                                {m.category}
                                            </span>
                                        </td>
                                        <td className="has-text-centered">
                                            {certifiedMap[m.metricId ?? m.id]
                                                ? <span className="tag is-success is-light is-small">✓ Certified</span>
                                                : <span className="has-text-grey">—</span>}
                                        </td>
                                        <td className="has-text-right">
                                            <div style={{ display: 'inline-flex', gap: '0.35rem' }}>
                                                <button type="button"
                                                    className="button is-small is-ghost"
                                                    aria-label="Edit metric"
                                                    style={{ padding: '0 0.4rem' }}
                                                    disabled={!editMode || deletingId != null}
                                                    onClick={editMode ? () => openEdit(m) : undefined}>
                                                    <Icon icon={faPencil} />
                                                </button>
                                                <button type="button"
                                                    className="button is-small is-ghost has-text-danger"
                                                    aria-label="Remove metric"
                                                    style={{ padding: '0 0.4rem' }}
                                                    disabled={!editMode || deletingId === (m.metricId ?? m.id)}
                                                    onClick={editMode ? () => setConfirmDelete(m) : undefined}>
                                                    <Icon icon={faTrash} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
            </StateHandler>

            <MetricAddModal isActive={addModalOpen} isSubmitting={isSubmitting}
                            existingMetricIds={new Set(metrics.map(m => m.metricId))}
                            existingMetrics={metrics}
                            onClose={() => setAddModalOpen(false)} onSubmit={handleAddSubmit} />
            <MetricEditModal isActive={editModalOpen} editMetric={editMetric} isSubmitting={isSubmitting}
                             onClose={() => setEditModalOpen(false)} onSubmit={handleEditSubmit} />

            <ConfirmDialog
                isActive={confirmDelete !== null}
                title="Remove Metric"
                message={`Remove "${confirmDelete?.label}"? ${confirmDelete?.category === 'Primary' || confirmDelete?.category === 'PRIMARY' ? "This is your Primary metric — removing it will affect the experiment's analysis." : 'This cannot be undone.'}`}
                confirmLabel="Remove"
                confirmClass="is-danger"
                onConfirm={() => { handleDelete(confirmDelete); setConfirmDelete(null); }}
                onCancel={() => setConfirmDelete(null)}
            />
        </>
    );
};

export default MetricsTab;
